/** @format */

import {ReactNode, useEffect, useMemo, useReducer, useState} from 'react';

import {Close, PlayArrow} from '@mui/icons-material';
import Circle from '@mui/icons-material/Circle';
import Square from '@mui/icons-material/Square';
import {Box, Button, Grid2, Typography} from '@mui/material';
import {clsx} from 'clsx';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import SidebarNew from 'components/MediaplanSidebar/SidebarNew';
import theme from 'theme';

import SidebarEdit from 'components/MediaplanSidebar/SidebarEdit';

import BackToProjectButton from 'components/BackToProjectButton';

import {YalperPlatform} from 'graphql/generated/types';

import {DuplicateForm} from './components/DuplicateForm';
import {SensorDistributeModal} from './components/SensorDistributeModal';
import {SensorGroup} from './components/SensorGroup';
import {SensorListModal} from './components/SensorListModal';
import logoImg from './images/logo-small-dark-theme.svg';

import {
  GetCurrentUserQueryPage,
  useDefaultSensorGroupsQuery,
  useGetCurrentUserQuery,
  useUpdateCompanySensorGroupsMutation,
} from './graphql/__generated__';
import {classes, StyledBox, StyledTertiaryMediumButton} from './styles';

type Sensor = string;

export type SensorsGroupsFromQuery = NonNullable<
  NonNullable<GetCurrentUserQueryPage['currentUser']>['company']
>['sensorGroups'];

type Grouped = {
  red: string[];
  yellow: string[];
  gray: string[];
};

interface ActionPayload {
  sensors: Sensor[];
  name?: string;
  grouped?: SensorsGroupsFromQuery;
}

interface ActionProps {
  type: string;
  payload: ActionPayload;
}

export interface Group {
  name: string;
  icon: ReactNode;
  sensors: Sensor[];
}

interface StateProps {
  groups: Group[];
  available: Sensor[];
}

const GroupName = {
  red: 'Критичные',
  yellow: 'Обычные',
  gray: 'Некритичные',
} as const;

const initial: Group[] = [
  {
    name: GroupName.red,
    icon: <Square className={clsx(classes.sensorGroupIcon, classes.redSensorsIcon)} />,
    sensors: [],
  },
  {
    name: GroupName.yellow,
    icon: <PlayArrow className={clsx(classes.sensorGroupIcon, classes.yellowSensorsIcon)} />,
    sensors: [],
  },
  {
    name: GroupName.gray,
    icon: <Circle className={clsx(classes.sensorGroupIcon, classes.graySensorsIcon)} />,
    sensors: [],
  },
];

function stateReducer({groups, available}: StateProps, {type, payload}: ActionProps): StateProps {
  if (type === 'init') {
    if (!payload.grouped) return {groups, available};

    const keys = Object.keys(GroupName) as Array<keyof typeof GroupName>;
    const updatedGroups = initial.map(initialGroup => {
      const group = keys.find(key => GroupName[key] === initialGroup.name);
      const sensors = payload.grouped?.[group as keyof SensorsGroupsFromQuery] || [];
      return {...initialGroup, sensors: [...sensors]};
    });

    const used = Object.values(payload.grouped).flat();
    const updatedAvailableSensors = payload.sensors.filter(name => !used.includes(name));

    return {groups: updatedGroups, available: updatedAvailableSensors};
  }

  if (type === 'set') {
    const updatedGroups = groups.map((group: Group) => {
      if (group.name !== payload.name) return group;
      return {...group, sensors: payload.sensors};
    });
    const updatedAvailableSensors = available.filter(name => !payload.sensors.includes(name));

    return {groups: updatedGroups, available: updatedAvailableSensors};
  }

  if (type === 'remove') {
    const updatedGroups = groups.map((group: Group) => {
      if (group.name !== payload.name) return group;
      return {
        ...group,
        sensors: group.sensors.filter(sensor => !payload.sensors.includes(sensor)),
      };
    });
    const updatedAvailableSensors = available.concat(payload.sensors);

    return {groups: updatedGroups, available: updatedAvailableSensors};
  }

  if (type === 'removeAll') {
    const updatedGroups = groups.map((group: Group) => {
      return {
        ...group,
        sensors: [],
      };
    });

    const updatedAvailableSensors = available.concat(groups.flatMap(group => group.sensors));

    return {groups: updatedGroups, available: updatedAvailableSensors};
  }

  return {groups, available};
}

const groupReducer = (acc: Grouped, {sensors, name}: Group): Grouped => {
  if (name === GroupName.red) {
    acc.red = acc.red.concat(sensors);
  } else if (name === GroupName.yellow) {
    acc.yellow = acc.yellow.concat(sensors);
  } else if (name === GroupName.gray) {
    acc.gray = acc.gray.concat(sensors);
  }
  return acc;
};

const Sensors = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const isEdit = searchParams.get('edit');

  const {
    loading: loadingData,
    error: errorData,
    data,
  } = useGetCurrentUserQuery({
    variables: {
      id: companyId || '',
    },
  });

  const {
    loading: loadingDefault,
    error: errorDefault,
    data: dataDefault,
  } = useDefaultSensorGroupsQuery();

  const [updateCompanySensorGroups] = useUpdateCompanySensorGroupsMutation();

  const [{groups, available}, dispatch] = useReducer(stateReducer, {
    groups: [],
    available: [],
  });

  const [allSensors, setAllSensors] = useState<string[]>([]);
  const [openDistributeModal, setOpenDistributeModal] = useState<boolean>(false);

  useEffect(() => {
    if (data) {
      const sensors = data.allSensors.map(el => el.name);
      const grouped: SensorsGroupsFromQuery = data.currentUser?.company?.sensorGroups || {
        red: [],
        yellow: [],
        gray: [],
      };
      dispatch({
        type: 'init',
        payload: {grouped, sensors},
      });
      setAllSensors(sensors);
    }
  }, [data]);

  const [selectedGroupIcon, setSelectedGroupIcon] = useState<ReactNode>(initial[0].icon);

  const [selectedGroupHeading, setSelectedGroupHeading] = useState(initial[0].name);
  const [isDisplayingModal, setIsDisplayingModal] = useState(false);
  const [isDisplayingForm, setIsDisplayingForm] = useState(false);

  const availableItems = useMemo(() => {
    const usedInGroups = groups.flatMap(group => group.sensors);
    return allSensors.filter(item => !usedInGroups.includes(item));
  }, [allSensors, groups]);

  const platformsByName = useMemo<ReadonlyMap<string, ReadonlyArray<YalperPlatform>>>(
    () => new Map(data?.allSensors.map(s => [s.name, s.platforms]) ?? []),
    [data],
  );

  const sensorsUnused = t('plurals.sensors_unused', {count: available.length});

  const loading = loadingData || loadingDefault;
  const error = errorData || errorDefault;

  if (loading || error) return null;

  const handleModalOpen = (groupName: string) => {
    const selectedGroup = groups.find(el => el.name === groupName);
    if (!selectedGroup) return;
    setSelectedGroupIcon(selectedGroup.icon);
    setSelectedGroupHeading(selectedGroup.name);
    setIsDisplayingModal(true);
  };

  const handleModalSave = (selectedSensors: Set<string>) => {
    const preselected = groups.find(el => el.name === selectedGroupHeading);
    const payload = {
      name: selectedGroupHeading,
      sensors: Array.from(selectedSensors).concat(preselected?.sensors || []),
    };
    dispatch({type: 'set', payload});
  };

  const handleSensorsRemove = (name: string, id: string) => {
    const payload = {name, sensors: [id]};
    dispatch({type: 'remove', payload});
  };

  const handleModalClose = () => {
    setIsDisplayingModal(false);
  };

  const handleSubmitSensors = () => {
    const sensorGroups = groups.reduce(groupReducer, {
      red: [],
      yellow: [],
      gray: [],
    });

    if (companyId) {
      updateCompanySensorGroups({
        variables: {id: companyId, sensorGroups},
      });
    }

    navigate('/home/main/status');
  };

  const handleFormOpen = () => {
    setIsDisplayingForm(true);
  };

  const handleFormClose = () => {
    setIsDisplayingForm(false);
  };

  const handleDistributeModalClose = () => {
    setOpenDistributeModal(false);
  };

  const handleDistributeModalOpen = () => {
    setOpenDistributeModal(true);
  };

  const handleRemoveAll = () => {
    dispatch({
      type: 'removeAll',
      payload: {sensors: []},
    });
  };

  const confirmDistribute = () => {
    handleDistributeModalClose();
    handleRemoveAll();
    dispatch({
      type: 'set',
      payload: {
        name: GroupName.red,
        sensors: [...(dataDefault?.defaultSensorGroups.red || [])],
      },
    });
    dispatch({
      type: 'set',
      payload: {
        name: GroupName.yellow,
        sensors: [...(dataDefault?.defaultSensorGroups.yellow || [])],
      },
    });
    dispatch({
      type: 'set',
      payload: {
        name: GroupName.gray,
        sensors: [...(dataDefault?.defaultSensorGroups.gray || [])],
      },
    });
  };

  const handleDistribute = () => {
    const selectedSensorsCount = groups.reduce((acc, cur) => {
      return acc + cur.sensors.length;
    }, 0);

    if (selectedSensorsCount) {
      handleDistributeModalOpen();
    } else {
      confirmDistribute();
    }
  };

  return (
    <>
      {isEdit ? (
        <SidebarEdit activeStep={'SENSORS'} enableAll />
      ) : (
        <SidebarNew activeStep={'SENSORS'} enableAll />
      )}

      <StyledBox>
        <Box className={classes.pageContainer}>
          <Typography className={classes.pageHeader}>
            <Trans>Сенсоры</Trans>
          </Typography>
          <Box className={classes.pageContent}>
            <Typography variant='body2'>
              <Trans>Чтобы сенсоры медиаплана работали, нужно настроить их важность</Trans>
            </Typography>
            <Box className={classes.sensorDescriptionWrapper}>
              <Square
                className={classes.sensorDescriptionIcon}
                htmlColor={theme.palette.figma_red_1?.main}
              />
              <Typography variant='body2'>
                <Trans>
                  Исправляйте в первую очередь — это самые болезненные для бюджета проблемы
                </Trans>
              </Typography>
            </Box>
            <Box className={classes.sensorDescriptionWrapper}>
              <PlayArrow
                className={clsx(classes.sensorDescriptionIcon, classes.yellowSensorsIcon)}
                htmlColor={theme.palette.figma_brown_1?.main}
              />
              <Typography variant='body2'>
                <Trans>
                  Возвращайтесь к ним после критичных — срочности нет, но откладывать надолго не
                  стоит
                </Trans>
              </Typography>
            </Box>
            <Box className={classes.sensorDescriptionWrapper}>
              <Circle
                className={classes.sensorDescriptionIcon}
                htmlColor={theme.palette.figma_gray_middle_dark?.main}
              />
              <Typography variant='body2'>
                <Trans>
                  Можно пропустить, если нет времени — на ключевые метрики почти не влияют
                </Trans>
              </Typography>
            </Box>
          </Box>

          <Grid2 container spacing={4}>
            <Grid2 size={{xs: 12, sm: 4}}>
              <StyledTertiaryMediumButton onClick={handleFormOpen}>
                <img src={logoImg} alt='AdSensor' />
                <Typography variant='body2'>
                  <Trans>Скопировать на другие проекты</Trans>
                </Typography>
              </StyledTertiaryMediumButton>
            </Grid2>

            <Grid2 size={{xs: 12, sm: 3.5}}>
              <StyledTertiaryMediumButton onClick={handleDistribute}>
                <Box
                  component='span'
                  className={clsx('material-symbols-outlined', classes.tenancyIcon)}
                >
                  tenancy
                </Box>
                <Typography variant='body2'>
                  <Trans>Распределить стандартно</Trans>
                </Typography>
              </StyledTertiaryMediumButton>
            </Grid2>

            <Grid2 size={{xs: 12, sm: 2}}>
              <StyledTertiaryMediumButton onClick={handleRemoveAll}>
                <Close sx={{fontSize: '16px'}} />
                <Typography variant='body2'>
                  <Trans>Очистить</Trans>
                </Typography>
              </StyledTertiaryMediumButton>
            </Grid2>
          </Grid2>

          <Box className={classes.sensorGroupsContainer}>
            {groups.map(({name, icon, sensors}) => (
              <SensorGroup
                key={name}
                name={name}
                icon={icon}
                sensors={sensors}
                platformsByName={platformsByName}
                onRemove={handleSensorsRemove}
                onAdd={handleModalOpen}
              />
            ))}
          </Box>
          <Box className={classes.unusedSensorsAmount}>{sensorsUnused}</Box>
          <Box className={classes.saveAllButtonWrapper}>
            <Button className={classes.saveAllButton} onClick={handleSubmitSensors}>
              <Trans>Сохранить медиаплан и вернуться на страницу проекта</Trans>
            </Button>
            {isEdit && <BackToProjectButton />}
          </Box>
        </Box>
        {isDisplayingModal && (
          <SensorListModal
            icon={selectedGroupIcon}
            heading={selectedGroupHeading}
            onClose={handleModalClose}
            onSave={handleModalSave}
            list={allSensors}
            groups={groups}
            platformsByName={platformsByName}
            availableItems={availableItems}
          />
        )}
        {openDistributeModal && (
          <SensorDistributeModal
            onClose={handleDistributeModalClose}
            onConfirm={confirmDistribute}
          />
        )}
        {isDisplayingForm && <DuplicateForm onClose={handleFormClose} companyId={companyId} />}
      </StyledBox>
    </>
  );
};

export default Sensors;
